import {
	Fragment,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
	type FC,
} from 'react';
import fish from '~/assets/icons/fish.svg';
import fishing from '~/assets/icons/fishing_2.svg';
import { FishingStages } from '../../views/HobbyFishing/HobbyFishing.model';
import {
	FISHING_MAX,
	FISHING_MIN,
	generateRandomPosition,
	getIndicatorDifference,
} from './Indicator.model';
import {
	StyledCatchIndicator,
	StyledCatchIndicatorWrapper,
	StyledContainer,
	StyledFishIconContainer,
	StyledFishingContainer,
	StyledGoodIndicator,
	StyledInnerContainer,
	StyledLowIndicator,
} from './Indicator.styled';

const SUCCESS_COLOR = '#1F8034';
const ERROR_COLOR = '#C71E1E';

const interpolateColor = (from: string, to: string, t: number) => {
	const clampedT = Math.max(0, Math.min(1, t));

	const fromNum = parseInt(from.slice(1), 16);
	const toNum = parseInt(to.slice(1), 16);

	const fromR = (fromNum >> 16) & 0xff;
	const fromG = (fromNum >> 8) & 0xff;
	const fromB = fromNum & 0xff;

	const toR = (toNum >> 16) & 0xff;
	const toG = (toNum >> 8) & 0xff;
	const toB = toNum & 0xff;

	const r = Math.round(fromR + (toR - fromR) * clampedT);
	const g = Math.round(fromG + (toG - fromG) * clampedT);
	const b = Math.round(fromB + (toB - fromB) * clampedT);

	return `rgb(${r}, ${g}, ${b})`;
};

type IndicatorProps = {
	stage: FishingStages;
	fishingProgress: number;
	handleThrow: (difference: number) => void;
	errorProgress: number;
};

const Indicator: FC<IndicatorProps> = ({
	stage,
	fishingProgress,
	handleThrow,
	errorProgress,
}) => {
	const [position, setPosition] = useState(generateRandomPosition());
	const fishingPositionRef = useRef(FISHING_MIN);
	const fishingContainerRef = useRef<HTMLDivElement | null>(null);
	const [hasThrown, setHasThrown] = useState(false);

	useEffect(() => {
		if (stage === FishingStages.NotStarted) {
			const randomPosition = generateRandomPosition();
			setPosition(randomPosition);
			setHasThrown(false);
			let direction = 1;
			let currentPosition = FISHING_MIN;
			fishingPositionRef.current = currentPosition;

			const interval = setInterval(() => {
				const container = fishingContainerRef.current;

				if (!container) {
					return;
				}

				container.style.left = `${currentPosition}px`;

				if (currentPosition >= FISHING_MAX) {
					direction = -1;
				} else if (currentPosition <= FISHING_MIN) {
					direction = 1;
				}

				currentPosition += direction * 9;
				fishingPositionRef.current = currentPosition;
			}, 10);

			return () => clearInterval(interval);
		}
	}, [stage]);

	useLayoutEffect(() => {
		if (stage === FishingStages.Waiting && !hasThrown) {
			const difference = getIndicatorDifference(
				fishingPositionRef.current,
				position,
			);
			handleThrow(difference);
			setHasThrown(true);
		}
	}, [stage, handleThrow, position, hasThrown]);

	return (
		<StyledContainer>
			<StyledInnerContainer />
			{stage === FishingStages.NotStarted && (
				<Fragment>
					<StyledLowIndicator style={{ left: position }}>
						<StyledGoodIndicator />
					</StyledLowIndicator>
					<StyledFishingContainer
						ref={fishingContainerRef}
						style={{ left: FISHING_MIN }}
					>
						<div className="dot" />
						<img src={fishing} />
						<div className="dot" />
					</StyledFishingContainer>
				</Fragment>
			)}
			{stage === FishingStages.Waiting && (
				<StyledFishIconContainer>
					<img src={fish} />
				</StyledFishIconContainer>
			)}
			{(stage === FishingStages.CatchFishTypeA ||
				stage === FishingStages.CatchFishTypeB) && (
				<Fragment>
					<StyledFishIconContainer>
						<img src={fish} />
					</StyledFishIconContainer>
					<StyledCatchIndicatorWrapper>
						<StyledCatchIndicator
							style={{
								width: `${fishingProgress}%`,
								backgroundColor:
									fishingProgress < 20
										? ERROR_COLOR
										: interpolateColor(
												SUCCESS_COLOR,
												ERROR_COLOR,
												errorProgress,
										  ),
							}}
						>
							<div className="dot" />
						</StyledCatchIndicator>
					</StyledCatchIndicatorWrapper>
				</Fragment>
			)}
		</StyledContainer>
	);
};

export { Indicator };
